import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  MEASUREMENT_WINDOW_CAPTION,
  type FirstScanFindingView,
} from '../../domain/safetyCases/firstScanFindingPresenter';

function Fact({
  label,
  value,
  testId,
  muted,
}: {
  label: string;
  value: string;
  testId: string;
  muted?: boolean;
}) {
  return (
    <Box data-testid={testId}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.04 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: muted ? 500 : 700 }} color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

export function FirstSafetyScanFindingCard({
  view,
}: {
  view: FirstScanFindingView;
}) {
  const historical = Boolean(view.historical && !view.empty);
  const [expanded, setExpanded] = useState(false);
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="first-safety-scan-finding" data-density={view.empty ? 'empty' : (historical ? 'historical' : 'current')}>
      <CardContent>
        {historical ? (
          <Box data-testid="first-safety-scan-finding-collapsed">
            <Box
              component="button"
              type="button"
              data-testid="first-safety-scan-finding-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
              sx={{
                cursor: 'pointer',
                border: 0,
                background: 'none',
                p: 0,
                textAlign: 'left',
                width: '100%',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
                  First Scan
                </Typography>
                {view.signed ? (
                  <Chip size="small" label="Signed" data-testid="first-safety-scan-finding-signed" />
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Historical · {view.windowLabel || 'earliest eligible Scan'}
                </Typography>
              </Stack>
            </Box>
            {expanded ? (
              <Box sx={{ mt: 1.25 }}>
                <FirstScanFindingFacts view={view} />
              </Box>
            ) : null}
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
                {view.empty ? 'First Safety Scan finding' : 'First Safety Scan finding'}
              </Typography>
              {view.signed ? (
                <Chip size="small" label="Signed" data-testid="first-safety-scan-finding-signed" />
              ) : null}
            </Stack>
            {view.empty ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }} data-testid="first-safety-scan-finding-empty">
                {view.emptyLabel}
              </Typography>
            ) : (
              <FirstScanFindingFacts view={view} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FirstScanFindingFacts({ view }: { view: FirstScanFindingView }) {
  return (
    <>
      <Box
        sx={{
          mt: 1.25,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        <Fact label="Where" value={view.whereLabel} testId="first-safety-scan-finding-where" />
        <Fact label={MEASUREMENT_WINDOW_CAPTION} value={view.whenLabel} testId="first-safety-scan-finding-when" />
        <Fact
          label="Observed"
          value={view.observedLabel}
          testId="first-safety-scan-finding-observed"
          muted={/not recorded/i.test(view.observedLabel)}
        />
        <Fact
          label="Coverage"
          value={view.rateLabel || view.coverageLabel}
          testId="first-safety-scan-finding-coverage"
          muted={/not recorded/i.test(view.coverageLabel) && !view.rateLabel}
        />
        <Fact
          label="Pattern"
          value={view.recurrenceLabel}
          testId="first-safety-scan-finding-pattern"
          muted={/not recorded/i.test(view.recurrenceLabel)}
        />
        <Fact label="Finding" value={view.findingLabel} testId="first-safety-scan-finding-finding" />
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <Fact
          label="Time concentration"
          value={view.concentrationLabel}
          testId="first-safety-scan-finding-concentration"
          muted={/no clear time concentration/i.test(view.concentrationLabel)}
        />
      </Box>
      {view.windowLabel ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }} data-testid="first-safety-scan-finding-window">
          Scan window · {view.windowLabel}
        </Typography>
      ) : null}
      {view.openScanHref ? (
        <Box sx={{ mt: 1.75 }}>
          <Button
            component={RouterLink}
            to={view.openScanHref}
            variant="contained"
            data-testid="first-safety-scan-finding-open"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Open Safety Scan
          </Button>
        </Box>
      ) : null}
    </>
  );
}
